import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { Box, Button, Typography } from '@mui/material';
import { FC, memo, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { CourseDeliveryCountsResponse, CourseDeliveryDay, CourseDeliveryRow } from 'src/entities/Report.entity';
import { usePageAutoSplit } from 'src/pages/reports/usePageAutoSplit';

import style from './print.module.css';

// DOM 実測による動的分割の上限 (A4 本体高さ) を超えない範囲で、初期分割の粒度として使う。
// 実測が発動しない Jest (jsdom) 下ではこの行数がそのまま保たれる。
export const ROWS_PER_PAGE = 45;

type Props = {
  data: CourseDeliveryCountsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  issueDate: string;
  issueTime: string;
};

export type PageInfo = {
  day: CourseDeliveryDay;
  rows: CourseDeliveryRow[];
};

/**
 * 配送日ごとに ROWS_PER_PAGE 行で初期ページ分割する (DOM 実測の初期状態)。
 * 行が 0 件の日は表示しない（API 側で既に除外されている想定）。
 */
export function buildPages(days: CourseDeliveryDay[]): PageInfo[] {
  const pages: PageInfo[] = [];
  days.forEach((day) => {
    if (day.rows.length === 0) return;
    for (let i = 0; i < day.rows.length; i += ROWS_PER_PAGE) {
      pages.push({ day, rows: day.rows.slice(i, i + ROWS_PER_PAGE) });
    }
  });
  return pages;
}

// 和式区切り（全角空白 U+3000）は eslint no-irregular-whitespace を避けるため Unicode escape を使う
const IDEOGRAPHIC_SPACE = '\u3000';

/** 'YYYY-MM-DD' → 'YYYY年MM月DD日（全角空白）配送' */
export function formatJapaneseDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${y}年${m}月${d}日${IDEOGRAPHIC_SPACE}配送`;
}

const CourseDeliveryCountReportPresenter: FC<Props> = memo(({ data, isLoading, isError, issueDate, issueTime }) => {
  const initialPages = useMemo(() => buildPages(data?.days ?? []), [data]);

  const getRows = useCallback((page: PageInfo) => page.rows, []);
  const replaceRows = useCallback(
    (page: PageInfo, rows: CourseDeliveryRow[]): PageInfo => ({ ...page, rows }),
    [],
  );

  const { pages, pageRefs } = usePageAutoSplit<PageInfo, CourseDeliveryRow>({
    initialPages,
    getRows,
    replaceRows,
  });

  const total = Math.max(pages.length, 1);

  return (
    <>
      <Helmet>
        <title>コース別配送件数リスト</title>
      </Helmet>
      <div className={style.shell} role="region" aria-label="コース別配送件数リスト プレビュー">
        <div className={style.toolbar}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => window.close()}
          >
            戻る
          </Button>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            コース別配送件数リスト
          </Typography>
          <Box className={style.toolbarRight}>
            <Typography variant="caption" color="text.secondary">全 {total} ページ</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
            >
              印刷
            </Button>
          </Box>
        </div>

        <div className={style.body}>
          {isLoading && <div className={style.empty}>読み込み中...</div>}
          {isError && <div className={style.empty}>データの取得に失敗しました。</div>}
          {!isLoading && !isError && pages.length === 0 && (
            <div className={style.empty}>該当するデータはありません。</div>
          )}
          {pages.map(({ day, rows }, idx) => (
            <div
              className={style.page}
              style={{ page: 'A4CourseDeliveryCount' }}
              key={`${day.delivery_date}-${idx}`}
              ref={(el) => { pageRefs.current[idx] = el; }}
            >
              <div className={style.header}>
                <div className={style.title}>コース別配送件数リスト</div>
                <div className={style.sub}>{formatJapaneseDate(day.delivery_date)}</div>
                <div className={style.meta}>
                  発行日付: {issueDate}<br />
                  発行時刻: {issueTime}<br />
                  ページ: {idx + 1} / {total}
                </div>
              </div>
              <table className={style.table}>
                <thead>
                  <tr>
                    <th style={{ width: '8%' }}>コース</th>
                    {day.show_rotation && <th style={{ width: '8%' }}>回転数</th>}
                    <th style={{ width: '32%' }}>運送会社名</th>
                    <th>ドライバー名</th>
                    <th style={{ width: '10%' }}>配送件数</th>
                    <th style={{ width: '10%' }}>伝票数</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIdx) => (
                    // 同一キーの可能性が低いが保険的に index 付与
                    <tr key={`${row.course_number ?? 'nil'}-${row.driver_name}-${rowIdx}`}>
                      <td className={style.center}>{row.course_number ?? ''}</td>
                      {day.show_rotation && (
                        <td className={style.center}>
                          {row.rotation_count >= 2 ? row.rotation_count : ''}
                        </td>
                      )}
                      <td>{row.carrier_company}</td>
                      <td>{row.driver_name}</td>
                      <td className={style.num}>{row.delivery_count}</td>
                      <td className={style.num}>{row.invoice_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </>
  );
});

export default CourseDeliveryCountReportPresenter;
